<template>
    <Modal
        modal-name="counselling-upsert-modal"
        modal-class="counselling-upsert-modal"
        size="lg"
        :title="$t('counselling.upsertModal.title')"
        :backdrop-close="false"
    >
        <div class="mb-4 fwb" v-html="$t('counselling.upsertModal.warning')" />
        <div v-if="errorMessage.length > 0" class="flash-message flash-message--error mb-4">{{ errorMessage }}</div>
        <div v-if="showSuccessMessage" class="flash-message flash-message--success mb-4" data-cy="question-success">
            {{ $t('counselling.upsertModal.successMessage') }}
        </div>
        <form @submit.prevent="onSubmit">
            <div class="row">
                <div class="col-12 col-md-6">
                    <label>{{ $t('counselling.upsertModal.yourEmail') }}</label>
                    <input v-model="email" name="email" class="input-text" data-cy="question-email" />
                </div>
                <div class="col-12 col-md-6 mt-3 mt-md-0">
                    <label>{{ $t('counselling.upsertModal.titleOfQuestion') }}</label>
                    <input v-model="title" name="title" class="input-text" data-cy="question-title" />
                </div>
            </div>
            <div class="row mt-3">
                <div class="col-12">
                    <label>{{ $t('counselling.upsertModal.text') }}</label>
                    <textarea
                        v-model="text"
                        name="text"
                        cols="15"
                        data-cy="question-text"
                        :placeholder="$t('counselling.upsertModal.textPlaceholder')"
                    />
                </div>
            </div>

            <div class="checkbox-container">
                <input id="checkbox" v-model="checkbox" type="checkbox" data-cy="checkbox" />
                <label for="checkbox" v-html="$t('counselling.upsertModal.checkboxAgreeText')"> </label>
            </div>

            <!--- Honeypot field -->
            <div class="honey">
                <label class="honey" for="username"></label>
                <input v-model="honeyText" class="honey" autocomplete="off" type="text" name="username" />
            </div>

            <div class="text-right mt-4">
                <button class="cursor-point">{{ $t('counselling.upsertModal.send') }}</button>
            </div>
        </form>
    </Modal>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import Modal from '~/components/Modal.vue'
import UpsertCounsellingPostRequest from '~/models/Api/Counselling/UpsertCounsellingPostRequest'

const email = ref('')
const title = ref('')
const text = ref('')
const honeyText = ref('')
const checkbox = ref(false)
const errorMessage = ref('')
const showSuccessMessage = ref(false)

onMounted(() => {
    useNuxtApp().$bus.$on('showCounsellingUpsertModal', () => {
        showSuccessMessage.value = false
        email.value = ''
        title.value = ''
        text.value = ''
        honeyText.value = ''
        errorMessage.value = ''
        checkbox.value = false
        useNuxtApp().$bus.$emit('showModal', 'counselling-upsert-modal')
    })
})

const i18n = useI18n()
const onSubmit = async () => {
    if (honeyText.value.length !== 0) {
        return
    }

    if (email.value.length === 0) {
        errorMessage.value = i18n.t('counselling.upsertModal.errors.emailRequired')

        return
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email.value)) {
        errorMessage.value = i18n.t('counselling.upsertModal.errors.emailInvalid')

        return
    }
    if (title.value.length === 0) {
        errorMessage.value = i18n.t('counselling.upsertModal.errors.titleRequired')

        return
    }
    if (title.value.includes('@')) {
        errorMessage.value = i18n.t('counselling.upsertModal.errors.titleCannotIncludeAt')

        return
    }
    if (text.value.length === 0) {
        errorMessage.value = i18n.t('counselling.upsertModal.errors.textRequired')

        return
    }
    if (checkbox.value === false) {
        errorMessage.value = i18n.t('counselling.upsertModal.errors.checkboxRequired')

        return
    }

    const request: UpsertCounsellingPostRequest = {
        email: email.value,
        title: title.value,
        text: text.value,
    }
    const response = await useNuxtApp().$repository.upsertCounsellingPost(request)
    if (response.result) {
        email.value = ''
        title.value = ''
        text.value = ''
        honeyText.value = ''
        errorMessage.value = ''
        checkbox.value = false
        showSuccessMessage.value = true
    }
}
</script>

<style lang="scss" scoped>
.honey {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    height: 0;
    width: 0;
    z-index: -1;
}

label {
    margin-bottom: 10px;
}

input.input-text {
    width: 100%;
    border-radius: 10px;
    background: $color-grey;
    font-size: 14px;
    line-height: 39px;
    font-weight: 400;
    padding: 0 15px;
    border: 1px solid transparent;
}

textarea {
    width: 100%;
    border-radius: 10px;
    background: $color-grey;
    font-size: 14px;
    line-height: 39px;
    font-weight: 400;
    padding: 0 15px;
    border: 1px solid transparent;
}

button {
    background: $color-cta;
    border-radius: 100px;
    padding: 10px 30px;
    border: 0;
    color: $color-white;
    font-size: 15px;
    font-weight: 500;
    margin-left: auto;
}

.checkbox-container {
    margin: 20px 0 0;
    display: flex;
    align-items: center;
    gap: 7px;
    position: relative;

    input {
        display: none;
    }

    input[type='checkbox']:checked + label:before {
        background-color: $color-purple;
        border-color: $color-purple;
    }

    input[type='checkbox']:checked + label:after {
        border-color: $color-white;
        border-style: solid;
        border-width: 0 2px 2px 0;
        bottom: 0;
        content: '';
        height: 10px;
        left: 10px;
        position: absolute;
        top: 7px;
        transform: rotate(45deg);
        width: 5px;
    }

    label {
        margin: 0;
        padding-left: 34px;
        line-height: 26px;

        &:before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            width: 26px;
            height: 26px;
            border: 2px solid $color-grey-dark;
            border-radius: 10px;
        }
    }
}

.flash-message {
    border-radius: 50px;
    padding: 10px 20px;

    &--success {
        background-color: $color-green;
        color: $color-green-dark;
    }

    &--error {
        background-color: $color-alert;
        color: $color-white;
    }
}
</style>
